import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Input,
  Field,
} from "@fluentui/react-components";
import { EditRegular } from "@fluentui/react-icons";
import { QuoteUpdateDto } from "library/types/types";

const StyledDialogSurface = styled(DialogSurface)`
  max-width: 500px;
  width: 90vw;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0px;
  gap: 8px;
`;

const RightButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

interface EditDeductiblesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (updates: Partial<QuoteUpdateDto>) => void;
  onReset: () => void;
  insurer: string;
  variantIndex: number;
  deductible?: string;
  deductibleSecondAccident?: string;
  deductibleGlass?: string;
  deductibleTheft?: string;
  deductibleTotalLoss?: string;
}

export const EditDeductiblesDialog: React.FC<EditDeductiblesDialogProps> = ({
  open,
  onClose,
  onSave,
  onReset,
  insurer,
  variantIndex,
  deductible: initialDeductible,
  deductibleSecondAccident: initialDeductibleSecondAccident,
  deductibleGlass: initialDeductibleGlass,
  deductibleTheft: initialDeductibleTheft,
  deductibleTotalLoss: initialDeductibleTotalLoss,
}) => {
  const { t } = useTranslation();

  const [deductible, setDeductible] = useState("");
  const [deductibleSecondAccident, setDeductibleSecondAccident] = useState("");
  const [deductibleGlass, setDeductibleGlass] = useState("");
  const [deductibleTheft, setDeductibleTheft] = useState("");
  const [deductibleTotalLoss, setDeductibleTotalLoss] = useState("");

  useEffect(() => {
    if (open) {
      setDeductible(initialDeductible || "");
      setDeductibleSecondAccident(initialDeductibleSecondAccident || "");
      setDeductibleGlass(initialDeductibleGlass || "");
      setDeductibleTheft(initialDeductibleTheft || "");
      setDeductibleTotalLoss(initialDeductibleTotalLoss || "");
    }
  }, [
    open,
    initialDeductible,
    initialDeductibleSecondAccident,
    initialDeductibleGlass,
    initialDeductibleTheft,
    initialDeductibleTotalLoss,
  ]);

  const handleSave = () => {
    const updates: Partial<QuoteUpdateDto> = {};

    // Only include fields that have actually changed
    const newDeductible = deductible || null;
    if (newDeductible !== (initialDeductible ?? null)) {
      updates.Deductible = newDeductible;
    }

    const newDeductibleSecondAccident = deductibleSecondAccident || null;
    if (
      newDeductibleSecondAccident !== (initialDeductibleSecondAccident ?? null)
    ) {
      updates.DeductibleSecondAccident = newDeductibleSecondAccident;
    }

    const newDeductibleGlass = deductibleGlass || null;
    if (newDeductibleGlass !== (initialDeductibleGlass ?? null)) {
      updates.DeductibleGlass = newDeductibleGlass;
    }

    const newDeductibleTheft = deductibleTheft || null;
    if (newDeductibleTheft !== (initialDeductibleTheft ?? null)) {
      updates.DeductibleTheft = newDeductibleTheft;
    }

    const newDeductibleTotalLoss = deductibleTotalLoss || null;
    if (newDeductibleTotalLoss !== (initialDeductibleTotalLoss ?? null)) {
      updates.DeductibleTotalLoss = newDeductibleTotalLoss;
    }

    onSave(updates);
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(e, data) => data.open || onClose()}>
      <StyledDialogSurface>
        <DialogBody>
          <DialogTitle>
            <EditRegular style={{ marginRight: "8px" }} />
            {t("qtg.casco.editDeductibles.title", "Edit Variant Fields")}
          </DialogTitle>
          <DialogContent>
            <FormGrid>
              <div>
                <strong>
                  {t("qtg.casco.editDeductibles.insurer", "Insurer")}:
                </strong>{" "}
                {insurer}
              </div>
              <div>
                <strong>
                  {t("qtg.casco.editDeductibles.variant", "Variant")}:
                </strong>{" "}
                V{variantIndex + 1}
              </div>

              <Field
                label={t("qtg.casco.table.deductible", "Standard Deductible")}
              >
                <Input
                  value={deductible}
                  onChange={(e) => setDeductible(e.target.value)}
                  placeholder={t(
                    "qtg.casco.editDeductibles.deductiblePlaceholder",
                    "e.g., 100 EUR"
                  )}
                />
              </Field>

              <Field
                label={t(
                  "qtg.casco.table.deductibleSecondAccident",
                  "Second Accident"
                )}
              >
                <Input
                  value={deductibleSecondAccident}
                  onChange={(e) => setDeductibleSecondAccident(e.target.value)}
                  placeholder={t(
                    "qtg.casco.editDeductibles.deductiblePlaceholder",
                    "e.g., 140 EUR"
                  )}
                />
              </Field>

              <Field label={t("qtg.casco.table.deductibleGlass", "Glass")}>
                <Input
                  value={deductibleGlass}
                  onChange={(e) => setDeductibleGlass(e.target.value)}
                  placeholder={t(
                    "qtg.casco.editDeductibles.glassPlaceholder",
                    "e.g., 0%, 600 EUR limits"
                  )}
                />
              </Field>

              <Field label={t("qtg.casco.table.deductibleTheft", "Theft")}>
                <Input
                  value={deductibleTheft}
                  onChange={(e) => setDeductibleTheft(e.target.value)}
                  placeholder={t(
                    "qtg.casco.editDeductibles.deductiblePlaceholder",
                    "e.g., 15%"
                  )}
                />
              </Field>

              <Field
                label={t("qtg.casco.table.deductibleTotalLoss", "Total Loss")}
              >
                <Input
                  value={deductibleTotalLoss}
                  onChange={(e) => setDeductibleTotalLoss(e.target.value)}
                  placeholder={t(
                    "qtg.casco.editDeductibles.deductiblePlaceholder",
                    "e.g., 0%"
                  )}
                />
              </Field>
            </FormGrid>
          </DialogContent>
        </DialogBody>
        <FooterActions>
          <Button appearance="subtle" onClick={handleReset}>
            {t("qtg.common.reset", "Reset")}
          </Button>
          <RightButtonGroup>
            <Button appearance="secondary" onClick={onClose}>
              {t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {t("qtg.common.save", "Save")}
            </Button>
          </RightButtonGroup>
        </FooterActions>
      </StyledDialogSurface>
    </Dialog>
  );
};
